import type { ViewModelEntity } from '@view-model/domain/view-model';
import { WorkspaceMemberRole, WorkspaceMemberRoles } from '@workspace/domain/workspace';
import { RTDBPath } from 'balus-common';
import type { UserId } from 'balus-common';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { MemberEditModal } from './MemberEditModal';
import type { GroupMembers, ViewModelIndividualPermissions, WorkspaceMembers } from './types';
import { ObjectRepository } from '@framework/repository';
import { UserPublicProfile } from '@user/PublicProfile';
import { MemberAddModal } from './MemberAddModal';
import { GroupMemberRoles } from '@group/domain/GroupEntity/GroupMemberRoles';
import type { GroupMemberRole } from '@group/domain';
import { MemberRemoveModal } from './MemberRemoveModal';

const fetchProfiles = async (userIds: UserId[]) => {
  const profiles = await Promise.all(
    userIds.map((userId) => new ObjectRepository(UserPublicProfile, RTDBPath.User.publicProfilePath(userId)).get())
  );
  return profiles.filter((profile) => !!profile);
};

type TargetModal = 'Add' | 'Edit' | 'Remove';

type Props = {
  isOpen: boolean;
  onClose(): void;
  viewModel: ViewModelEntity;
};

export const ConfigureIndividualPermissions = ({ isOpen, onClose, viewModel }: Props) => {
  const [targetModal, setTargetModal] = useState<TargetModal>('Edit');
  const handleAddMember = useCallback(() => setTargetModal('Add'), []);
  const handleRemoveMember = useCallback(() => setTargetModal('Remove'), []);
  const handleCancel = useCallback(() => setTargetModal('Edit'), []);

  const handleClose = useCallback(() => {
    setTargetModal('Edit');
    onClose();
  }, [onClose]);

  const [workspaceMembers, setWorkspaceMembers] = useState<WorkspaceMembers | null>(null);

  useEffect(() => {
    const repo = new ObjectRepository(WorkspaceMemberRoles, RTDBPath.Workspace.memberRolesPath(viewModel.workspaceId));
    repo.addListener(async (roles) => {
      if (!roles) {
        setWorkspaceMembers([]);
        return;
      }

      const adminUsers = (await fetchProfiles(roles.userIdsByRole(WorkspaceMemberRole.admin)))
        .filter((u) => !!u)
        .sort(UserPublicProfile.compare);
      const editorUsers = (await fetchProfiles(roles.userIdsByRole(WorkspaceMemberRole.editor)))
        .filter((u) => !!u)
        .sort(UserPublicProfile.compare);
      const viewerUsers = (await fetchProfiles(roles.userIdsByRole(WorkspaceMemberRole.viewer)))
        .filter((u) => !!u)
        .sort(UserPublicProfile.compare);

      setWorkspaceMembers(
        [...adminUsers, ...editorUsers, ...viewerUsers].map((user) => [
          user,
          roles.getRoleOf(user.id) as WorkspaceMemberRole, // ロール情報は必ず存在するのでcastする
        ])
      );
    });
    return () => repo.removeListener();
  }, [viewModel.workspaceId]);

  const [groupMembers, setGroupMembers] = useState<GroupMembers | null>(null);

  useEffect(() => {
    const repo = new ObjectRepository(GroupMemberRoles, RTDBPath.Group.memberRolesPath(viewModel.groupId));
    repo.addListener(async (roles) => {
      if (!roles) {
        setGroupMembers([]);
        return;
      }

      const regularUsers = (await fetchProfiles(roles.regularUserIds()))
        .filter((u) => !!u)
        .sort(UserPublicProfile.compare);
      const guestUsers = (await fetchProfiles(roles.guestUserIds())).filter((u) => !!u).sort(UserPublicProfile.compare);

      setGroupMembers(
        [...regularUsers, ...guestUsers].map((user) => [
          user,
          roles.getRoleOf(user.id) as GroupMemberRole, // ロール情報は必ず存在するのでcastする
        ])
      );
    });
    return () => repo.removeListener();
  }, [viewModel.groupId]);

  const guestUserIds = useMemo(
    () => new Set(groupMembers?.filter(([, role]) => role === 'guest').map(([profile]) => profile.id)),
    [groupMembers]
  );

  const [individualPermissions, setIndividualPermissions] = useState<ViewModelIndividualPermissions | null>(null);

  useEffect(() => {
    fetchProfiles(Object.keys(viewModel.individualPermissions)).then((profiles) => {
      setIndividualPermissions(
        profiles
          .sort(UserPublicProfile.compare)
          .map((profile) => [profile, viewModel.individualPermissions[profile.id]])
      );
    });
  }, [viewModel.individualPermissions]);

  if (!isOpen) return;
  if (!workspaceMembers) return;
  if (!groupMembers) return;
  if (!individualPermissions) return;

  return (
    <>
      <MemberEditModal
        isOpen={targetModal === 'Edit'}
        onClose={handleClose}
        viewModel={viewModel}
        workspaceMembers={workspaceMembers}
        guestUserIds={guestUserIds}
        individualPermissions={individualPermissions}
        onAddMember={handleAddMember}
        onRemoveMember={handleRemoveMember}
      />

      <MemberAddModal
        isOpen={targetModal === 'Add'}
        onClose={handleCancel}
        viewModel={viewModel}
        workspaceMembers={workspaceMembers}
        groupMembers={groupMembers}
        individualPermissions={individualPermissions}
      />

      <MemberRemoveModal
        isOpen={targetModal === 'Remove'}
        onClose={handleCancel}
        viewModel={viewModel}
        individualMembers={individualPermissions}
        guestUserIds={guestUserIds}
      />
    </>
  );
};
